const IP_LOOKUP_URL = "https://www.cloudflare.com/cdn-cgi/trace";

/**
 * Every event waits for this lookup, so a stalled request must not hold
 * events back for longer than this
 */
export const IP_LOOKUP_TIMEOUT_MS = 2000;

let ipAddressPromise: Promise<string> | null = null;

const lookupIpAddress = async (signal?: AbortSignal): Promise<string> => {
  try {
    const response = await fetch(IP_LOOKUP_URL, { signal });
    const text = await response.text();
    const ipLine = text.split("\n").find((line) => line.startsWith("ip="));
    return ipLine ? ipLine.substring(3) : "";
  } catch {
    return "";
  }
};

const fetchIpAddress = async (): Promise<string> => {
  const controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  // Covers a stalled request as well as a stalled response body
  const timeout = new Promise<string>((resolve) => {
    timeoutId = setTimeout(() => {
      controller?.abort();
      resolve("");
    }, IP_LOOKUP_TIMEOUT_MS);
  });

  try {
    return await Promise.race([lookupIpAddress(controller?.signal), timeout]);
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Looks up the client IP address once and caches the result. Resolves to an
 * empty string if the lookup fails or takes longer than IP_LOOKUP_TIMEOUT_MS.
 */
export const getIpAddress = (): Promise<string> => {
  if (!ipAddressPromise) ipAddressPromise = fetchIpAddress();
  return ipAddressPromise;
};

export const resetIpAddressCache = () => {
  ipAddressPromise = null;
};
